import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Clock, SunMedium, ClipboardList, Bell, ArrowRight, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { API } from '../../config/apiBase';


export default function ColaboradorDashboardPage() {
    const { user } = useAuth();
    const [registros, setRegistros] = useState([]);
    const [empresa, setEmpresa] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) return;
        const h = { 'Authorization': `Bearer ${token}` };
        Promise.all([
            fetch(`${API}/ponto/registros/today`, { headers: h }).then(r => r.json()),
            fetch(`${API}/ponto/empresa`, { headers: h }).then(r => r.json()),
        ]).then(([reg, emp]) => {
            setRegistros(Array.isArray(reg) ? reg : []); setEmpresa(emp); setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    const now = new Date();
    const hora = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    const lastReg = registros[registros.length - 1];
    const status = !lastReg ? 'Aguardando entrada' : lastReg.tipo === 'entrada' ? 'Trabalhando' : 'Turno encerrado';
    const statusColor = status === 'Trabalhando' ? '#10B981' : status === 'Turno encerrado' ? '#6B7280' : '#F59E0B';
    const entradas = registros.filter(r => r.tipo === 'entrada').length;
    const saídas = registros.filter(r => r.tipo === 'saída').length;

    return (
        <div className="space-y-5" data-testid="colaborador-dashboard">
            {/* Welcome + Status */}
            <div className="rounded-xl p-5" style={{ background: 'linear-gradient(135deg, rgba(var(--sv-accent-rgb), 0.15) 0%, rgba(var(--sv-accent-rgb), 0.03) 100%)', border: '1px solid rgba(var(--sv-accent-rgb), 0.2)' }}>
                <div className="flex items-start justify-between">
                    <div>
                        <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>
                            Olá, {user?.name?.split(' ')[0]}
                        </h2>
                        <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                            {now.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </p>
                        {empresa?.name && <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted)' }}>{empresa.name}</p>}
                    </div>
                    <div className="text-right">
                        <p className="text-2xl font-bold" style={{ color: 'var(--sv-gold-primary)', fontFamily: "var(--font-display)" }}>{hora}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                    <div className="w-2 h-2 rounded-full" style={{ background: statusColor }} />
                    <span className="text-xs font-medium" style={{ color: statusColor }}>{status}</span>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl p-3.5 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <p className="text-xl font-bold" style={{ color: 'var(--text)' }}>{registros.length}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Registros</p>
                </div>
                <div className="rounded-xl p-3.5 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <p className="text-xl font-bold" style={{ color: '#10B981' }}>{entradas}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Entradas</p>
                </div>
                <div className="rounded-xl p-3.5 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <p className="text-xl font-bold" style={{ color: '#EF4444' }}>{saídas}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Saídas</p>
                </div>
            </div>

            {/* Última marcação */}
            {lastReg && (
                <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: lastReg.tipo === 'entrada' ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)' }}>
                        <Clock size={18} style={{ color: lastReg.tipo === 'entrada' ? '#10B981' : '#EF4444' }} />
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Última marcação: {lastReg.tipo === 'entrada' ? 'Entrada' : 'Saída'}</p>
                        <p className="text-xs" style={{ color: 'var(--muted)' }}>
                            {new Date(lastReg.timestamp_server || lastReg.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </p>
                    </div>
                    <CheckCircle size={16} style={{ color: '#10B981' }} />
                </div>
            )}

            {/* Ações Rapidas */}
            <div className="space-y-2">
                {[
                    { label: 'Bater Ponto', desc: 'Registrar entrada ou saída', href: '/sistema/ponto', icon: Clock, color: 'var(--sv-gold-primary)', primary: true },
                    { label: 'Meu Dia', desc: 'Detalhes do turno', href: '/sistema/colaborador/meu-dia', icon: SunMedium, color: '#F59E0B' },
                    { label: 'Meu Histórico', desc: 'Ver marcações anteriores', href: '/sistema/colaborador/histórico', icon: ClipboardList, color: '#3B82F6' },
                    { label: 'Solicitações', desc: 'Pedir ajuste de ponto', href: '/sistema/colaborador/solicitações', icon: Bell, color: '#8B5CF6' },
                ].map((a) => (
                    <a key={a.label} href={a.href} className="flex items-center gap-3 p-3.5 rounded-xl transition-all hover:scale-[1.01]"
                        style={{ background: a.primary ? 'rgba(var(--sv-accent-rgb), 0.08)' : 'var(--surface)', border: `1px solid ${a.primary ? 'rgba(var(--sv-accent-rgb), 0.25)' : 'var(--border)'}` }}
                        data-testid={`action-${a.label.toLowerCase().replace(/\s/g,'-')}`}>
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${a.color}15` }}>
                            <a.icon size={16} style={{ color: a.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{a.label}</p>
                            <p className="text-[10px]" style={{ color: 'var(--muted)' }}>{a.desc}</p>
                        </div>
                        <ArrowRight size={14} style={{ color: 'var(--muted)' }} />
                    </a>
                ))}
            </div>
        </div>
    );
}
